import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, HardHat, FileWarning } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { PageHeader, Loading, EmptyState } from '../components/Shared';
import { ReviewBadge, SourceNote, SampleDataBanner } from '../components/DataBadge';

const STATUS_STYLES = {
  planned: 'bg-slate-100 text-slate-800 border-slate-300',
  in_progress: 'bg-amber-50 text-amber-900 border-amber-300',
  complete: 'bg-emerald-50 text-emerald-900 border-emerald-300',
  delayed: 'bg-red-50 text-red-900 border-red-300',
  needs_review: 'bg-[#FFF7E6] text-[#7A4E00] border-[#F2D08A]',
};

export default function InfrastructureTracker() {
  const [content, setContent] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/infrastructure-tracker').then((r) => setContent(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (!content) return <EmptyState title="Infrastructure Tracker unavailable" subtitle="Please try again shortly." />;

  const statuses = content.statuses || {};
  const projects = (content.projects || []).filter((p) => statusFilter === 'all' || p.status === statusFilter);

  return (
    <div>
      <PageHeader kicker="ROADS, BRIDGES & CAPITAL PROJECTS" title="Infrastructure Tracker" subtitle="What was promised, what it costs, and where it stands, tracked in the open for Ward 8." />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <SampleDataBanner text='Projects marked "Sample" or "Needs Review" are placeholders or resident-reported items, NOT confirmed City of Kawartha Lakes projects. Verified project details will be entered as sources are confirmed.' testId="infra-sample-banner" />

        <p className="text-base text-slate-700 leading-relaxed mt-6 max-w-3xl" data-testid="infra-intro">{content.intro}</p>

        {/* Status filter chips */}
        <div className="flex gap-2 overflow-x-auto py-5 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="infra-status-filters">
          <button onClick={() => setStatusFilter('all')} className={`shrink-0 h-11 px-4 rounded-full text-sm font-bold border transition-colors duration-150 ${statusFilter === 'all' ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid="infra-filter-all">All</button>
          {Object.entries(statuses).map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} className={`shrink-0 h-11 px-4 rounded-full text-sm font-bold border transition-colors duration-150 ${statusFilter === v ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid={`infra-filter-${v}`}>{l}</button>
          ))}
        </div>

        {projects.length === 0 ? (
          <EmptyState title="No projects match this filter" subtitle="Try another status, or check back as verified projects are added." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((p) => (
              <Card key={p.id} className="card-lift h-full" data-testid="infra-project-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <Badge variant="outline" className={`${STATUS_STYLES[p.status] || STATUS_STYLES.planned} font-semibold text-xs`} data-testid={`infra-status-${p.status}`}>{statuses[p.status] || p.status}</Badge>
                    <ReviewBadge status={p.review_status} />
                  </div>
                  <h3 className="font-serif font-bold text-[#1a2744] text-lg leading-snug mt-2.5 flex items-start gap-2"><HardHat className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" /> {p.name}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                    {p.community && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.community}</span>}
                    {p.year && <span>Target: {p.year}</span>}
                    {p.cost && <span>Cost: {p.cost}</span>}
                    {!p.cost && <span className="text-amber-800 font-semibold">Cost: to be confirmed</span>}
                  </div>
                  <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{p.summary}</p>
                  <SourceNote sourceName={p.source_name} sourceUrl={p.source_url} sourceNote={p.source_note} lastUpdated={p.last_updated} testId="infra-project-source" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-10 border-[#C9A84C]" data-testid="infra-cta-card">
          <CardContent className="p-6 sm:p-8">
            <FileWarning className="h-8 w-8 text-[#1a2744] mb-2" />
            <h3 className="font-serif font-black text-[#1a2744] text-lg">See a road or bridge that needs attention?</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">Report it through the Issue Tracker. Resident reports help build the public record of what Ward 8 infrastructure actually needs.</p>
            <Button asChild className="mt-4 h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="infra-report-cta">
              <Link to="/issue-tracker/report?category=roads">Report a Road Issue</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
